import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, MapPin, Globe, Mail, Phone, User, Lock, Loader2,
  BadgeCheck, ShieldCheck, Pencil, Save, X, ArrowLeft, Package,
  ChevronLeft, ChevronRight, Heart, MessageSquare, Megaphone, Send
} from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import Sidebar from '../components/Sidebar';
import TradeSceneBackdrop from '../components/TradeSceneBackdrop';
import TradeRoutesBackground from '../components/TradeRoutesBackground';
import { countryFlag } from '../utils/countryFlags';
import { countryRibbonGradient } from '../lib/countryColors';
import useReveal from '../hooks/useReveal';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PublicCommentForm from '../components/PublicCommentForm';
import DirectoryCommentList from '../components/DirectoryCommentList';
import DirectoryShareBox from '../components/DirectoryShareBox';
import '../styles/epic.css';

const BRAND_BLUE = '#2B4B6A';

const isLoggedIn = () => !!localStorage.getItem('token');

/**
 * Company logo with a graceful fallback: many imported listings store a website
 * URL (or a dead link) in logo_url, which renders as a broken image. On error we
 * swap to a branded monogram instead.
 */
const CompanyLogo = ({ company }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = company.logo_url;
  // Attempt ANY http(s) value. Requiring a file extension wrongly rejected valid
  // CDN logos served from extension-less paths (e.g. .../pictures/<id>/picture).
  // Listings that store a website URL here simply fail to decode and fall back.
  const canTry = typeof src === 'string' && /^https?:\/\//i.test(src.trim()) && !failed;

  const initials = (company.name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  // Monogram sits underneath; a logo that decodes successfully covers it. That
  // way there is never a broken-image flash while the real logo is loading.
  return (
    <div className="relative w-full h-full">
      <div
        className="absolute inset-0 rounded-xl flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E3550 100%)` }}
        aria-hidden={loaded ? 'true' : 'false'}
        aria-label={loaded ? undefined : company.name}
      >
        <span className="text-white font-extrabold text-3xl tracking-tight select-none">{initials}</span>
      </div>
      {canTry && (
        <img
          src={src}
          alt={company.name}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          style={{ background: company.logo_bg || '#F3F4F6' }}
          className={`absolute inset-0 w-full h-full object-contain rounded-xl transition-opacity duration-200 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
};

/** Count-up number used in the stats strip. */
const CountUpNumber = ({ value = 0, className = '' }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || target <= 0) {
      setShown(target);
      return undefined;
    }
    const duration = 900;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutCubic
      setShown(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className}>{shown}</span>;
};

const MaskedRow = ({ icon: Icon, value }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <Icon className="w-4 h-4 text-gray-300 flex-shrink-0" />
    <div className="flex-1 text-sm text-gray-500 font-mono tracking-tight select-none">
      {value || <span className="inline-block h-3.5 w-40 max-w-full rounded bg-gray-100 blur-[1px] align-middle" />}
    </div>
    <Lock className="w-3.5 h-3.5 text-gray-300" />
  </div>
);

const ContactRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="flex-1 text-sm text-gray-700 break-words">{children}</div>
  </div>
);

const DirectoryCompanyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isCompanyAdmin = ['admin', 'main_admin', 'company_admin'].includes(user?.role);
  // Use token (sync) not user (async) — see DirectorySearch note. Logged-in
  // visitors always see the admin shell.
  const Shell = token ? Sidebar : PublicLayout;
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitForm, setSubmitForm] = useState({});
  const [submittingBusiness, setSubmittingBusiness] = useState(false);

  const loggedIn = isLoggedIn();

  const loadComments = useCallback(async () => {
    try {
      const c = await api.get(`/public/directory/${slug}/comments`);
      setComments(c.data.items || []);
    } catch (_) {
      setComments([]);
    }
  }, [slug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ownerToken = new URLSearchParams(window.location.search).get('owner_token') || '';
      const res = await api.get(`/public/directory/${slug}`, { params: ownerToken ? { owner_token: ownerToken } : {} });
      setCompany(res.data);
      if (res.data.owner_preview) {
        // Owner-preview link (from outreach email): contact fields arrive unlocked
        setContact({
          website: res.data.website,
          emails: res.data.emails || [],
          phones: res.data.phones || [],
          principal_name: res.data.principal_name,
          principal_email: res.data.principal_email,
        });
      }
      setLikesCount(res.data.likes_count || 0);
      setLiked(false);
      document.title = `${res.data.name} | Trados Directory`;
      loadComments();
      if (isLoggedIn()) {
        try {
          const c = await api.get(`/directory/${slug}/contact`);
          setContact(c.data);
        } catch (_) { /* contact stays locked */ }
        try {
          const l = await api.get(`/directory/${slug}/like`);
          setLiked(!!l.data.liked);
          setLikesCount(l.data.likes_count || 0);
        } catch (_) { /* like state stays default */ }
      } else {
        // Anonymous visitors get their own like state, keyed on a hashed
        // visitor id server-side rather than on an account.
        try {
          const l = await api.get(`/public/directory/${slug}/like`);
          setLiked(!!l.data.liked);
          setLikesCount(l.data.likes_count || 0);
        } catch (_) { /* like state stays default */ }
      }
    } catch (e) {
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [slug, loadComments]);

  useEffect(() => { load(); }, [load]);

  // Cinematic scroll reveals — re-scans after the listing and comments render
  useReveal([loading, company?.slug, comments.length, contact]);

  const handleLike = async () => {
    setLiking(true);
    try {
      const path = isLoggedIn() ? `/directory/${slug}/like` : `/public/directory/${slug}/like`;
      const res = await api.post(path);
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count || 0);
    } catch (e) {
      toast.error('Could not update like.');
    } finally {
      setLiking(false);
    }
  };

  const handleSubmitBusiness = async () => {
    if (!(submitForm.name || '').trim()) {
      toast.error('Company name is required.');
      return;
    }
    setSubmittingBusiness(true);
    try {
      const res = await api.post('/directory/submit', {
        name: submitForm.name,
        website: submitForm.website || null,
        emails: toList(submitForm.emails),
        country: submitForm.country || null,
        city: submitForm.city || null,
        commodities: toList(submitForm.commodities),
        phones: toList(submitForm.phones),
        description: submitForm.description || null,
      });
      toast.success(res.data.message || 'Business added.');
      setSubmitOpen(false);
      setSubmitForm({});
      navigate(`/directory/${res.data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit business.');
    } finally {
      setSubmittingBusiness(false);
    }
  };

  const handlePostComment = async () => {
    const text = commentText.trim();
    if (text.length < 3) {
      toast.error('Comment is too short.');
      return;
    }
    setPostingComment(true);
    try {
      const res = await api.post(`/directory/${slug}/comments`, { text });
      toast.success(res.data.message || 'Comment submitted for review.');
      setCommentText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit comment.');
    } finally {
      setPostingComment(false);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const res = await api.post(`/directory/${slug}/claim`);
      if (res.data.auto_approved) {
        toast.success(res.data.message || 'Listing verified — you can now edit it.');
      } else {
        toast.success(res.data.message || 'Claim submitted for review.');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit claim.');
    } finally {
      setClaiming(false);
    }
  };

  const startEdit = () => {
    setForm({
      name: company.name || '',
      logo_url: company.logo_url || '',
      website: contact?.website || '',
      emails: (contact?.emails || []).join(', '),
      country: company.country || '',
      city: company.city || '',
      commodities: (company.commodities || []).join(', '),
      principal_name: contact?.principal_name || '',
      principal_email: contact?.principal_email || '',
      phones: (contact?.phones || []).join(', '),
      description: company.description || '',
    });
    setEditing(true);
  };

  const toList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        logo_url: form.logo_url || null,
        website: form.website || null,
        emails: toList(form.emails),
        country: form.country || null,
        city: form.city || null,
        commodities: toList(form.commodities),
        principal_name: form.principal_name || null,
        principal_email: form.principal_email || null,
        phones: toList(form.phones),
        description: form.description || null,
      };
      await api.put(`/directory/${slug}`, payload);
      toast.success('Listing updated.');
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
      </Shell>
    );
  }

  if (!company) {
    // Most people reach this from an outreach email about a listing that has
    // since been renamed or removed. Sending them back to a search box loses
    // them — offer the thing the email was asking for in the first place.
    return (
      <Shell>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <Building2 className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-800">Company not found</h1>
          <p className="mt-3 text-sm text-gray-500 leading-relaxed">
            This listing is no longer at this address. You can still create your free
            Trados account and add your company to the directory.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/register"
              data-testid="not-found-register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white w-full sm:w-auto"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              Create my free account
            </Link>
            <Link
              to="/directory"
              data-testid="not-found-browse"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
            >
              Browse the directory
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  const isOwner = contact?.is_owner;
  const claimStatus = contact?.claim_status || company.claim_status || 'unclaimed';

  return (
    <Shell>
      {/* Dark hero band — live trade-route map behind, company card overlapping */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#0F172A' }}>
        <TradeRoutesBackground homeCountry={company.country} />
        <div className="relative max-w-5xl mx-auto px-4 pt-8 pb-40">
          <div className="flex items-center justify-between gap-3">
            <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to directory
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={() => company.prev && navigate(`/directory/${company.prev.slug}`)}
                disabled={!company.prev}
                data-testid="directory-prev-company"
                title={company.prev?.name || ''}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white/80 border border-white/20 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <button
                onClick={() => company.next && navigate(`/directory/${company.next.slug}`)}
                disabled={!company.next}
                data-testid="directory-next-company"
                title={company.next?.name || ''}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white/80 border border-white/20 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/40 line-mask">
            <span>Trados Trading Company Directory</span>
          </p>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold text-white line-mask" style={{ animationDelay: '0.08s' }}>
            <span style={{ animationDelay: '0.08s' }}>{company.name}</span>
          </h2>
          <p className="mt-2 text-sm text-white/60 line-mask">
            <span style={{ animationDelay: '0.16s' }}>
              Trading {(company.commodities || []).slice(0, 3).join(' · ') || 'commodities'}
              {company.country ? ` — from ${company.country} to the world` : ''}
            </span>
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-20 pb-10">
        {/* Header card */}
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl shadow-slate-900/10 sheen reveal reveal-scale">
          {/* Verification header strip */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 md:px-8 py-3 border-b ${
              company.verification?.verified ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
            }`}
            data-testid="verification-header"
          >
            <div className="flex items-center gap-2">
              {company.verification?.verified ? (
                <ShieldCheck className="w-5 h-5" style={{ color: '#C9A227' }} />
              ) : (
                <BadgeCheck className="w-5 h-5 text-red-400" />
              )}
              <span className={`text-sm font-semibold ${company.verification?.verified ? 'text-green-800' : 'text-red-700'}`}>
                {company.verification?.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isOwner ? (
                <button onClick={startEdit} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: BRAND_BLUE }}>
                  <Pencil className="w-3.5 h-3.5" /> Edit listing
                </button>
              ) : claimStatus === 'pending' ? (
                <span className="text-xs text-gray-500">A claim for this business is pending review.</span>
              ) : claimStatus === 'approved' ? (
                <span className="text-xs text-gray-500">Claimed by its owner.</span>
              ) : loggedIn ? (
                <button onClick={handleClaim} disabled={claiming} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                  {claiming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />}
                  Claim this Business
                </button>
              ) : (
                <a href="/login" className="px-4 py-2 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: BRAND_BLUE }}>
                  Claim this Business
                </a>
              )}
            </div>
          </div>

          <div className="relative tsb-host p-6 md:p-8">
          <TradeSceneBackdrop />
          <div className="flex items-start gap-5">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden flex-shrink-0 p-2 float-loop lift">
              <CompanyLogo company={company} />
            </div>
            <div className="min-w-0 pt-1">
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <p className="text-gray-500 flex items-center gap-1.5 mt-1">
                <MapPin className="w-4 h-4" />
                {countryFlag(company.country) && (
                  <span className="text-base leading-none" aria-hidden="true">{countryFlag(company.country)}</span>
                )}
                {[company.city, company.country].filter(Boolean).join(', ') || 'Location not available'}
              </p>
              <button
                onClick={handleLike}
                disabled={liking}
                data-testid="directory-like-button"
                className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  liked
                    ? 'bg-rose-50 border-rose-200 text-rose-600'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Heart className={`w-4 h-4 ${liked ? 'fill-rose-500 text-rose-500' : ''}`} />
                {likesCount > 0 ? likesCount : 'Like'}
              </button>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-gray-100">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Share this company</h2>
            <DirectoryShareBox slug={slug} companyName={company.name} />
          </div>

          {company.commodities?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {company.commodities.map((c, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-blue-50 text-xs font-medium flex items-center gap-1" style={{ color: BRAND_BLUE }}>
                  <Package className="w-3 h-3" />{c}
                </span>
              ))}
            </div>
          )}

          {company.description && (
            <div className="mt-6 pt-5 border-t border-gray-100">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">About</h2>
              <p className="text-gray-700 leading-relaxed max-w-3xl whitespace-pre-line">{company.description}</p>
            </div>
          )}

          {/* Stats + country ribbon footer band — light green so white flag
              stripes stay visible, mirroring the verification header strip */}
          <div className="-mx-6 md:-mx-8 -mb-6 md:-mb-8 mt-7 px-6 md:px-8 pt-4 pb-5 bg-green-50 border-t border-green-100">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600 reveal" style={{ '--i': 0 }}>
              <Heart className="w-4 h-4 text-rose-400" />
              <CountUpNumber value={likesCount} className="font-semibold text-gray-900 tabular-nums" /> likes
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 reveal" style={{ '--i': 1 }}>
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <CountUpNumber value={comments.length} className="font-semibold text-gray-900 tabular-nums" /> comments
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 reveal" style={{ '--i': 2 }}>
              <Package className="w-4 h-4 text-gray-400" />
              <CountUpNumber value={(company.commodities || []).length} className="font-semibold text-gray-900 tabular-nums" /> commodities
            </div>
            {company.country && (
              <div className="flex items-center gap-2 text-sm text-gray-600 reveal" style={{ '--i': 3 }}>
                <MapPin className="w-4 h-4 text-gray-400" />
                {countryFlag(company.country)} {company.country}
              </div>
            )}
          </div>

          {/* Country ribbon — the company's flag colours close out the card */}
          <div
            className="country-ribbon mt-4 ring-1 ring-black/5"
            style={{ background: countryRibbonGradient(company.country) }}
            data-testid="country-ribbon"
            aria-label={company.country ? `${company.country} colours` : 'Trados colours'}
          />
          </div>
          </div>
        </div>

        {company.owner_preview && (
          <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="owner-preview-banner">
            <div className="flex-1">
              <p className="font-semibold text-gray-900">This is your company's listing — you're viewing it through your private owner link.</p>
              <p className="text-sm text-gray-600 mt-1">
                The contact details below are what traders see once they join Trados. Claim the listing free to control it, correct it, and unlock the Trados Verified badge.
              </p>
            </div>
            <a
              href={`/register?utm_source=owner_preview&company=${encodeURIComponent(company.slug || '')}`}
              className="px-5 py-3 rounded-lg text-sm font-bold text-white text-center flex-shrink-0"
              style={{ backgroundColor: '#F58A5A' }}
              data-testid="owner-preview-claim-btn"
            >
              Claim My Business — Free
            </a>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {/* Contact column */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 reveal reveal-left lift">
              <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-2">Contact information</h2>

              {!loggedIn && !company.owner_preview ? (
                <>
                  <div className="opacity-90">
                    {company.contact_available?.website && (
                      <MaskedRow icon={Globe} value={company.contact_preview?.website} />
                    )}
                    {(company.contact_preview?.emails || []).map((e, i) => (
                      <MaskedRow key={`me${i}`} icon={Mail} value={e} />
                    ))}
                    {(company.contact_preview?.phones || []).map((p, i) => (
                      <MaskedRow key={`mp${i}`} icon={Phone} value={p} />
                    ))}
                    {company.contact_available?.principal_name && (
                      <MaskedRow icon={User} value={company.contact_preview?.principal_name} />
                    )}
                    {!company.contact_available?.website && !(company.contact_preview?.emails || []).length &&
                      !(company.contact_preview?.phones || []).length && !company.contact_available?.principal_name && (
                      <p className="text-sm text-gray-400 py-3">No contact details on file yet.</p>
                    )}
                  </div>
                  <div className="mt-5 rounded-xl bg-[#FFFDE7] border border-yellow-200 p-4 text-center">
                    <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: BRAND_BLUE }} />
                    <p className="text-sm text-gray-700 font-medium">Log in to view contact details and reach out</p>
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <a href="/register" className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: BRAND_BLUE }}>
                        Create free account
                      </a>
                      <a href="/login" className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50">
                        Log in
                      </a>
                    </div>
                  </div>
                </>
              ) : contact?.paid_required ? (
                <>
                  <div className="opacity-90">
                    {(contact.preview?.website) && <MaskedRow icon={Globe} value={contact.preview.website} />}
                    {(contact.preview?.emails || []).map((e, i) => (
                      <MaskedRow key={`pe${i}`} icon={Mail} value={e} />
                    ))}
                    {(contact.preview?.phones || []).map((p, i) => (
                      <MaskedRow key={`pp${i}`} icon={Phone} value={p} />
                    ))}
                    {contact.preview?.principal_name && <MaskedRow icon={User} value={contact.preview.principal_name} />}
                  </div>
                  <div className="mt-5 rounded-xl bg-[#FFFDE7] border border-yellow-200 p-4 text-center" data-testid="contact-paid-upsell">
                    <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: BRAND_BLUE }} />
                    <p className="text-sm text-gray-700 font-medium">Contact details are available to paid Trados members</p>
                    {isCompanyAdmin ? (
                      <a href="/admin/billing" className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: BRAND_BLUE }}>
                        Upgrade membership
                      </a>
                    ) : (
                      <p className="text-xs text-gray-500 mt-2">Ask your company admin to upgrade your Trados membership.</p>
                    )}
                  </div>
                </>
              ) : contact ? (
                <div>
                  {contact.website ? (
                    <ContactRow icon={Globe}>
                      <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                         target="_blank" rel="noopener noreferrer"
                         className="hover:underline" style={{ color: BRAND_BLUE }}>
                        {contact.website}
                      </a>
                    </ContactRow>
                  ) : null}
                  {(contact.emails || []).map((e, i) => (
                    <ContactRow key={`e${i}`} icon={Mail}>
                      <a href={`mailto:${e}`} className="hover:underline" style={{ color: BRAND_BLUE }}>{e}</a>
                    </ContactRow>
                  ))}
                  {(contact.phones || []).map((p, i) => (
                    <ContactRow key={`p${i}`} icon={Phone}>
                      <a href={`tel:${p}`} className="hover:underline">{p}</a>
                    </ContactRow>
                  ))}
                  {contact.principal_name || contact.principal_email ? (
                    <ContactRow icon={User}>
                      {contact.principal_name}
                      {contact.principal_email ? (
                        <> · <a href={`mailto:${contact.principal_email}`} className="hover:underline" style={{ color: BRAND_BLUE }}>{contact.principal_email}</a></>
                      ) : null}
                    </ContactRow>
                  ) : null}
                  {!contact.website && !(contact.emails || []).length && !(contact.phones || []).length && !contact.principal_name && (
                    <p className="text-sm text-gray-400 py-3">No contact details on file yet.</p>
                  )}
                </div>
              ) : (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
              )}
            </div>

            {/* Comments */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mt-6 reveal lift" data-testid="directory-comments">
              <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-400" /> Comments
                {comments.length > 0 && <span className="text-gray-400 font-normal normal-case">({comments.length})</span>}
              </h2>

              {comments.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">No comments yet. Be the first to share your experience with this company.</p>
              ) : (
                <DirectoryCommentList
                  comments={comments}
                  slug={slug}
                  canReply={!!contact?.can_reply}
                  onChanged={loadComments}
                />
              )}

              {loggedIn ? (
                <div className="mt-4 flex items-start gap-2">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="Share your experience with this company…"
                    data-testid="directory-comment-input"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <button
                    onClick={handlePostComment}
                    disabled={postingComment || commentText.trim().length < 3}
                    data-testid="directory-comment-submit"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    {postingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Post
                  </button>
                </div>
              ) : (
                <PublicCommentForm slug={slug} />
              )}
              <p className="mt-2 text-[11px] text-gray-400">Comments are reviewed by the Trados team before they appear.</p>
            </div>
          </div>

          {/* Right column */}
          <div>
            {/* Verify CTA */}
            {!company.verification?.verified && (
              <div
                className="rounded-2xl p-6 text-white"
                data-testid="directory-verify-cta"
                style={{ backgroundColor: '#0F172A' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="w-5 h-5 text-yellow-300" />
                  <span className="text-sm font-bold">Verify your company with Trados</span>
                </div>
                <p className="text-xs text-white/85 leading-relaxed">
                  Get verified and start getting real clients. You will receive an official
                  Trados Verified banner to add to your site — and more business from our platform.
                </p>
                <a
                  href={loggedIn ? '/admin/verify/bronze' : '/register'}
                  className="mt-4 block text-center px-4 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ backgroundColor: '#F5C518', color: '#1A2E45' }}
                >
                  Start verification →
                </a>
              </div>
            )}

            {/* Submit your business */}
            <div className="bg-green-50 rounded-2xl border border-green-100 shadow-sm p-6 mt-6" data-testid="submit-business-box">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                <span className="text-sm font-semibold text-gray-800">Submit your business</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Not in the directory yet? Add your company and reach thousands of traders worldwide.
              </p>
              {loggedIn ? (
                <button
                  onClick={() => {
                    // Gate: must have a verified email to enter the moderation queue.
                    if (user && user.email_verified === false) {
                      navigate('/admin/verify-email-required');
                      return;
                    }
                    // Manage existing listing from the dashboard page (it shows status + form).
                    navigate('/admin/my-company-listing');
                  }}
                  data-testid="submit-business-button"
                  className="mt-4 w-full px-4 py-2.5 rounded-lg text-sm font-medium border hover:bg-gray-50"
                  style={{ color: BRAND_BLUE, borderColor: BRAND_BLUE }}
                >
                  Add my company
                </button>
              ) : (
                <a
                  href="/register?next=/admin/my-company-listing"
                  className="mt-4 block text-center w-full px-4 py-2.5 rounded-lg text-sm font-medium border hover:bg-gray-50"
                  style={{ color: BRAND_BLUE, borderColor: BRAND_BLUE }}
                >
                  Create account to submit
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Submit business modal */}
      {submitOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Submit your business</h3>
              <button onClick={() => setSubmitOpen(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
              {[
                ['name', 'Company name *'], ['website', 'Website'],
                ['emails', 'Emails (comma-separated)'], ['phones', 'Phones (comma-separated)'],
                ['country', 'Country'], ['city', 'City'],
                ['commodities', 'Commodities (comma-separated)'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input
                    value={submitForm[key] || ''}
                    onChange={(e) => setSubmitForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  value={submitForm.description || ''}
                  onChange={(e) => setSubmitForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  maxLength={600}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setSubmitOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSubmitBusiness} disabled={submittingBusiness} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                {submittingBusiness ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Submit business
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Edit listing</h3>
              <button onClick={() => setEditing(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
              {[
                ['name', 'Company name'], ['logo_url', 'Logo URL'],
                ['website', 'Website'], ['emails', 'Emails (comma-separated)'],
                ['country', 'Country'], ['city', 'City'],
                ['commodities', 'Commodities (comma-separated)'], ['phones', 'Phones (comma-separated)'],
                ['principal_name', 'Principal name'], ['principal_email', 'Principal email'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input
                    value={form[key] || ''}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
};

export default DirectoryCompanyDetail;
